import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Read a browser cookie by name (used for the JS-readable csrf_token).
function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(name + "="));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

const api = axios.create({
  baseURL: API_BASE,
  // Send httpOnly access_token cookie + JS-readable csrf_token cookie
  // on every same-origin API call.
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  // Attach CSRF header on mutating requests when a csrf_token cookie exists.
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = readCookie("csrf_token");
    if (csrf && !config.headers["X-CSRF-Token"]) {
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

// Paths where a 401 must NOT force-redirect to /login — public verify
// pages need to remain reachable even when the AuthContext bootstrap fails
// (they don't require auth in the first place).
const PUBLIC_PATHS = ["/verify-signature/", "/v/", "/maintenance", "/reset-password"];

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      const p = window.location.pathname;
      const onPublic = PUBLIC_PATHS.some((pref) => p.startsWith(pref));
      if (!onPublic && !p.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err, fallback = "Terjadi kesalahan") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  return String(detail) || fallback;
}

// PDF/file URLs are consumed by <iframe>/<a>/new-tab which auto-send the
// httpOnly access_token cookie on same-origin GETs. No query-param token
// needed anymore.
export function fileUrl(path) {
  return `${API_BASE}/files/${path}`;
}

export function spbPdfUrl(id, version) {
  // `version` (e.g. spb.updated_at) is a cache-buster so browser always
  // fetches a fresh PDF after edits/plate overrides.
  const suffix = version ? `?v=${encodeURIComponent(version)}` : "";
  return `${API_BASE}/spb/${id}/pdf${suffix}`;
}

export function exportXlsxUrl() {
  return `${API_BASE}/export/spb.xlsx`;
}

export function reportPdfUrl({ tanggal_from, tanggal_to } = {}) {
  const params = new URLSearchParams();
  if (tanggal_from) params.set("tanggal_from", tanggal_from);
  if (tanggal_to) params.set("tanggal_to", tanggal_to);
  const qs = params.toString();
  return `${API_BASE}/reports/spb.pdf${qs ? `?${qs}` : ""}`;
}

export default api;
